import { COUNTRY_ALIASES } from './countryAliases'

export type CompareFieldKey =
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'country'
  | 'dateOfBirth'
  | 'documentNumber'
  | 'expirationDate'

export type CompareField = {
  key: CompareFieldKey
  label: string
}

export const COMPARE_FIELDS: CompareField[] = [
  { key: 'firstName', label: 'First name' },
  { key: 'middleName', label: 'Middle name' },
  { key: 'lastName', label: 'Last name' },
  { key: 'country', label: 'Country' },
  { key: 'dateOfBirth', label: 'Date of birth' },
  { key: 'documentNumber', label: 'Document number' },
  { key: 'expirationDate', label: 'Expiration date' },
]

export type ExtractedFields = Partial<Record<CompareFieldKey, string | null>>

export type CompareRow = {
  key: CompareFieldKey
  label: string
  crmValue: string
  docValue: string
  crmRawValue: unknown
  docRawValue: unknown
  match: boolean
  status: 'match' | 'mismatch' | 'missing_in_crm' | 'missing_in_doc'
}

export type Comparison = {
  verdictOk: boolean
  isDocument: boolean
  allFieldsMatch: boolean
  summaryEn: string
  rows: CompareRow[]
}

export const isAppliable = (row: CompareRow): boolean =>
  !row.match &&
  row.docRawValue !== null &&
  row.docRawValue !== undefined &&
  String(row.docRawValue).trim() !== ''

export const valueForForm = (key: CompareFieldKey, raw: unknown): string => {
  const str = String(raw ?? '').trim()
  if (key === 'country') {
    const iso = normalizeCountry(str)
    return iso || str
  }
  return str
}

const isEmpty = (v: unknown): boolean => {
  if (v === null || v === undefined) return true
  if (typeof v === 'string' && v.trim() === '') return true
  return false
}

const displayValue = (v: unknown): string => {
  if (v === null || v === undefined) return 'Not found'
  if (typeof v === 'object') return JSON.stringify(v)
  const s = String(v).trim()
  return s.length === 0 ? 'Not found' : s
}

export const normalizeCountry = (v: unknown): string => {
  if (isEmpty(v)) return ''
  const normalized = String(v).trim().replace(/\s+/g, ' ').toUpperCase()
  const compact = normalized.replace(/\s+/g, '')
  if (Object.prototype.hasOwnProperty.call(COUNTRY_ALIASES, normalized)) {
    return COUNTRY_ALIASES[normalized]
  }
  if (Object.prototype.hasOwnProperty.call(COUNTRY_ALIASES, compact)) {
    return COUNTRY_ALIASES[compact]
  }
  return normalized
}

const normalizeField = (key: CompareFieldKey, v: unknown): string => {
  if (isEmpty(v)) return ''
  const str = String(v).trim()
  if (key === 'country') return normalizeCountry(str)
  if (key === 'documentNumber') return str.replace(/\s+/g, '').toUpperCase()
  if (key === 'dateOfBirth' || key === 'expirationDate') {
    const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(str)
    if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
    const dmy = /^(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(str)
    if (dmy) {
      const d = dmy[1].padStart(2, '0')
      const m = dmy[2].padStart(2, '0')
      return `${dmy[3]}-${m}-${d}`
    }
    return str.replace(/\s+/g, ' ').toLowerCase()
  }
  return str.replace(/\s+/g, ' ').toLowerCase()
}

const valuesMatch = (
  key: CompareFieldKey,
  crmVal: unknown,
  docVal: unknown,
): boolean => {
  const a = normalizeField(key, crmVal)
  const b = normalizeField(key, docVal)
  if (a === '' && b === '') return true
  return a === b
}

type LlmParsed = {
  is_document?: boolean
  summary_en?: string
  summary_ru?: string
  extracted_fields?: ExtractedFields
}

export const buildComparison = (
  crmItem: Record<string, unknown>,
  llmParsed: LlmParsed,
): Comparison => {
  const extracted =
    llmParsed && typeof llmParsed.extracted_fields === 'object' && llmParsed.extracted_fields !== null
      ? llmParsed.extracted_fields
      : ({} as ExtractedFields)

  const isDocument = Boolean(llmParsed && llmParsed.is_document === true)

  const rows: CompareRow[] = COMPARE_FIELDS.map((def) => {
    const crmVal = crmItem[def.key]
    const docVal = Object.prototype.hasOwnProperty.call(extracted, def.key)
      ? extracted[def.key]
      : null
    const match = valuesMatch(def.key, crmVal, docVal)
    const crmEmpty = isEmpty(crmVal)
    const docEmpty = isEmpty(docVal)
    let status: CompareRow['status'] = 'mismatch'
    if (match) status = 'match'
    else if (crmEmpty && !docEmpty) status = 'missing_in_crm'
    else if (!crmEmpty && docEmpty) status = 'missing_in_doc'
    return {
      key: def.key,
      label: def.label,
      crmValue: displayValue(crmVal),
      docValue: displayValue(docVal),
      crmRawValue: crmVal,
      docRawValue: docVal,
      match,
      status,
    }
  })

  const allFieldsMatch = rows.every((r) => r.match)
  const verdictOk = isDocument && allFieldsMatch
  let summaryEn = ''
  if (llmParsed && typeof llmParsed.summary_en === 'string') {
    summaryEn = llmParsed.summary_en
  } else if (llmParsed && typeof llmParsed.summary_ru === 'string') {
    summaryEn = llmParsed.summary_ru
  }

  return { verdictOk, isDocument, allFieldsMatch, summaryEn, rows }
}
